import { useMutation } from '@tanstack/react-query';
import { checkTeamName } from 'api/checkApi';
import { deleteTeam, updateTeam } from 'api/userApi';
import { queryClient } from 'App';
import FalconCardHeader from 'components/common/FalconCardHeader';
import Flex from 'components/common/Flex';
import LoadingButton from 'components/fantaclasse/LoadingButton';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Button, Card, Col, Form, Modal, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from 'use-debounce';
import * as Yup from 'yup';

const DeleteTeamModal = ({ showModal, setShowModal, teamName }) => {
  const navigate = useNavigate();
  const deleteTeamMutation = useMutation({
    mutationFn: deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success('La tua squadra è stata eliminata! 💔');
      navigate('/team');
    },
    onError: err => {
      toast.error(err.response?.data?.message || err.message);
    }
  });

  const handleDeleteTeam = async () => {
    deleteTeamMutation.mutate();
  };

  const [typedName, setTypedName] = useState('');

  return (
    <Modal show={showModal} onHide={() => setShowModal(false)} centered>
      <Modal.Header className="p-4">
        <Modal.Title>
          <h4 className="mb-0">
            Sei sicuro di voler eliminare la tua squadra?
          </h4>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <ul className="mb-0">
          <li className="mb-2">
            La tua squadra <strong>sarà eliminata</strong> e{' '}
            <strong>rimossa dalla classifica</strong>.
          </li>
          <li className="mb-2">
            Una volta eliminata,{' '}
            <strong>non potrà più essere recuperata</strong>.
          </li>
          <li>
            Dovrai creare una nuova squadra e{' '}
            <strong>ricominciare da zero</strong>.
          </li>
        </ul>
        <hr />
        <div className="px-3">
          <h4 className="fs-9">
            Per confermare la tua decisione, scrivi "<strong>{teamName}</strong>
            " qui sotto.
          </h4>
          <Form.Control
            value={typedName}
            onChange={e => setTypedName(e.target.value)}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowModal(false)}>
          Indietro
        </Button>
        <LoadingButton
          text="Conferma"
          variant="danger"
          isLoading={deleteTeamMutation.isPending}
          disabled={typedName !== teamName}
          action={handleDeleteTeam}
        />
      </Modal.Footer>
    </Modal>
  );
};

DeleteTeamModal.propTypes = {
  showModal: PropTypes.bool.isRequired,
  setShowModal: PropTypes.func.isRequired,
  teamName: PropTypes.string.isRequired
};

const TeamSettings = ({ team }) => {
  const [isCheckingTeamName, setIsCheckingTeamName] = useState(false);
  const [teamNameNotAvaiable, setTeamNameNotAvaiable] = useState(null);

  const formik = useFormik({
    initialValues: {
      name: team.name,
      description: team.description
    },
    validationSchema: Yup.object().shape({
      name: Yup.string()
        .min(4, 'Il nome deve contenere almeno 4 caratteri.')
        .max(30, 'Il nome deve contenere al massimo 30 caratteri.')
        .trim()
        .required('Il nome della squadra è obbligatorio.'),
      description: Yup.string()
        .max(150, 'La descrizione deve contenere al massimo 150 caratteri.')
        .trim()
    }),
    validateOnChange: true,
    onSubmit: async values => {
      updateTeamMutation.mutate(values);
    }
  });

  const [isUpdating, setIsUpdating] = useState(false);
  const updateTeamMutation = useMutation({
    mutationFn: updateTeam,
    onMutate: () => {
      setIsUpdating(true);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userData'] });
      toast.success(() => <span>La squadra è stata modificata!</span>);
    },
    onError: err => {
      toast.error(
        err.response?.data?.message ||
          'Errore durante la modifica del nome della squadra'
      );
    },
    onSettled: () => {
      setIsUpdating(false);
    }
  });

  const [debouncedTeamName] = useDebounce(formik.values.name, 800);

  useEffect(() => {
    if (!formik.isValid) {
      setIsCheckingTeamName(false);
      return;
    }
    if (debouncedTeamName && debouncedTeamName !== team.name) {
      const checkTeamNameAvaiability = async () => {
        setIsCheckingTeamName(true);
        try {
          const response = await checkTeamName(debouncedTeamName);
          if (response.status === 200) {
            setTeamNameNotAvaiable(null);
          } else if (response.status === 422) {
            setTeamNameNotAvaiable(
              `La squadra ${debouncedTeamName} esiste già. Scegli un altro nome.`
            );
          } else {
            toast.error('Errore di validazione del nome della squadra');
          }
        } catch (error) {
          toast.error(
            'Errore di rete durante il controllo della disponibilità del nome utente'
          );
        } finally {
          setIsCheckingTeamName(false);
        }
      };

      checkTeamNameAvaiability();
    } else {
      setTeamNameNotAvaiable(null);
      setIsCheckingTeamName(false);
    }
  }, [debouncedTeamName]);

  const handleSubmit = e => {
    e.preventDefault();
    formik.handleSubmit(e);
  };

  const [showDeleteTeamModal, setShowDeleteTeamModal] = useState(false);

  return (
    <Card className="mb-3">
      <DeleteTeamModal
        showModal={showDeleteTeamModal}
        setShowModal={setShowDeleteTeamModal}
        teamName={team.name}
      />
      <FalconCardHeader title="Squadra" />
      <Card.Body className="bg-body-tertiary">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} controlId="name">
              <Form.Label>Nome della squadra</Form.Label>
              <Form.Control
                name="name"
                type="text"
                value={formik.values.name}
                onChange={e => {
                  setIsCheckingTeamName(true);
                  formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                placeholder="Nome della squadra"
                isValid={
                  formik.values.name !== team.name &&
                  !isCheckingTeamName &&
                  formik.touched.name &&
                  !formik.errors.name &&
                  !teamNameNotAvaiable
                }
                isInvalid={
                  formik.values.name !== team.name &&
                  !isCheckingTeamName &&
                  formik.touched.name &&
                  (formik.errors.name || teamNameNotAvaiable)
                }
              />
              <Form.Control.Feedback>
                Il nome della squadra è disponibile.
              </Form.Control.Feedback>
              <Form.Control.Feedback type="invalid">
                {formik.errors.name || teamNameNotAvaiable}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Row className="mb-3 g-3">
            <Form.Group as={Col} controlId="teamDescription">
              <Form.Label>Descrizione della squadra</Form.Label>
              <Form.Control
                name="description"
                type="text"
                value={formik.values.description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Descrizione della squadra"
                isValid={
                  formik.values.description !== team.description &&
                  formik.touched.description &&
                  !formik.errors.description
                }
                isInvalid={
                  formik.values.description !== team.description &&
                  formik.touched.description &&
                  formik.errors.description
                }
              />
              <Form.Control.Feedback type="invalid">
                {formik.errors.description}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>
          <Flex justifyContent="between">
            <div>
              <Button
                variant="danger"
                onClick={() => setShowDeleteTeamModal(true)}
              >
                Elimina squadra
              </Button>
            </div>
            <LoadingButton
              text="Salva"
              isLoading={isUpdating}
              disabled={
                (formik.values.name === team.name &&
                  formik.values.description === team.description) ||
                !formik.isValid ||
                isCheckingTeamName ||
                teamNameNotAvaiable
              }
              type="submit"
            />
          </Flex>
        </Form>
      </Card.Body>
    </Card>
  );
};

TeamSettings.propTypes = {
  team: PropTypes.shape({
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired
  }).isRequired
};

export default TeamSettings;
